import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import apiService from '../../services/api';
import './CommonStyles.css';

const AIProductMapModal = ({ isOpen, onClose, onConfirm, currentItem }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [productOptions, setProductOptions] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      const initialTerm = currentItem?.extracted?.product_name || '';
      setSearchTerm(initialTerm);
      setSelectedProduct(null);
      handleSearch(initialTerm);
    }
  }, [isOpen, currentItem]);

  const handleSearch = async (inputValue) => {
    try {
      setIsLoading(true);
      const res = await apiService.getProducts(inputValue);
      const list = res.data || res || [];
      const options = list.map(p => ({
        value: p.product_id || p.id,
        variation_id: p.variation_id || (p.product_variations?.[0]?.variations?.[0]?.id) || null,
        label: p.text || p.name || p.sku || 'Unknown Product',
        sku: p.sku || p.sub_sku || '',
        unit_price: p.max_price || p.selling_price || p.unit_price || null,
        raw: p
      }));
      setProductOptions(options);

      // Auto select first match if exact or close
      if (options.length > 0 && !selectedProduct) {
        setSelectedProduct(options[0]);
      }
    } catch (err) {
      /* console.error('Failed to fetch products for mapping', err); */
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirm = () => {
    if (!selectedProduct) {
      toast.warning('Please select a product from the list.');
      return;
    }
    onConfirm(selectedProduct);
  };

  if (!isOpen) return null;

  const extractedName = currentItem?.extracted?.product_name || 'Unknown Product';
  const extractedSku = currentItem?.extracted?.sku || null;

  return (
    <div 
      className="modal-overlay" 
      style={{ 
        zIndex: 1000000, 
        backgroundColor: 'rgba(15, 23, 42, 0.65)', 
        backdropFilter: 'blur(8px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      <div 
        className="modal-container" 
        style={{ 
          maxWidth: '560px', 
          width: '92%', 
          maxHeight: '85vh',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: '16px',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          overflow: 'hidden',
          backgroundColor: '#ffffff',
          border: '1px solid #e2e8f0',
          animation: 'modalSlideIn 0.2s cubic-bezier(0.16, 1, 0.3, 1)'
        }}
      >
        {/* Modal Header */}
        <div 
          style={{ 
            background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
            padding: '1.25rem 1.5rem',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            color: '#ffffff',
            flexShrink: 0
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ fontSize: '1.25rem' }}>🔗</span>
            <div>
              <h2 style={{ fontSize: '1.1rem', margin: 0, fontWeight: 600, color: '#f8fafc' }}>
                Map Item to Catalog Product
              </h2>
              <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>
                Link AI invoice item to inventory product
              </span>
            </div>
          </div>
          <button 
            onClick={onClose} 
            style={{ 
              background: 'rgba(255,255,255,0.1)', 
              border: 'none', 
              color: '#94a3b8', 
              width: '32px', 
              height: '32px', 
              borderRadius: '50%', 
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '1rem',
              transition: 'all 0.15s ease'
            }}
            onMouseOver={(e) => e.currentTarget.style.color = '#ffffff'}
            onMouseOut={(e) => e.currentTarget.style.color = '#94a3b8'}
          >
            ✕
          </button>
        </div>

        {/* Modal Body */}
        <div style={{ padding: '1.5rem', overflowY: 'auto', flex: 1 }}>
          {/* Extracted Item Card */}
          <div 
            style={{ 
              backgroundColor: '#f1f5f9', 
              padding: '1rem 1.25rem', 
              borderRadius: '12px', 
              marginBottom: '1.25rem',
              border: '1px solid #e2e8f0'
            }}
          >
            <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', tracking: '0.05em', marginBottom: '0.25rem' }}>
              Extracted Invoice Line
            </div>
            <div style={{ fontWeight: 600, color: '#0f172a', fontSize: '0.95rem' }}>
              {extractedName}
            </div>
            {extractedSku && (
              <span style={{ display: 'inline-block', marginTop: '0.35rem', backgroundColor: '#e2e8f0', color: '#475569', padding: '2px 8px', borderRadius: '6px', fontSize: '0.75rem', fontWeight: 500 }}>
                SKU: {extractedSku}
              </span>
            )}
          </div>

          {/* Search Box */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, fontSize: '0.85rem', color: '#334155' }}>
              Search System Catalog
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type="text"
                placeholder="Type product name, SKU, or barcode..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  handleSearch(e.target.value);
                }}
                style={{ 
                  width: '100%', 
                  padding: '0.65rem 1rem', 
                  paddingRight: '2.5rem',
                  borderRadius: '10px', 
                  border: '1.5px solid #cbd5e1', 
                  fontSize: '0.9rem',
                  outline: 'none',
                  boxSizing: 'border-box',
                  transition: 'border-color 0.15s ease'
                }}
                onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                onBlur={(e) => e.target.style.borderColor = '#cbd5e1'}
              />
              {searchTerm && (
                <button
                  onClick={() => {
                    setSearchTerm('');
                    handleSearch('');
                  }}
                  style={{
                    position: 'absolute',
                    right: '10px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    color: '#94a3b8',
                    cursor: 'pointer',
                    fontSize: '0.9rem'
                  }}
                >
                  ✕
                </button>
              )}
            </div>
          </div>

          {/* Products List */}
          <div style={{ marginBottom: '0.5rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
              <span style={{ fontSize: '0.8rem', fontWeight: 600, color: '#64748b' }}>
                {isLoading ? 'Searching...' : `Matching Products (${productOptions.length})`}
              </span>
            </div>

            <div 
              style={{ 
                maxHeight: '170px', 
                overflowY: 'auto', 
                borderRadius: '10px', 
                border: '1px solid #cbd5e1',
                backgroundColor: '#ffffff'
              }}
            >
              {isLoading ? (
                <div style={{ padding: '2rem', textAlign: 'center', color: '#64748b', fontSize: '0.875rem' }}>
                  🔍 Searching products...
                </div>
              ) : productOptions.length === 0 ? (
                <div style={{ padding: '2rem', textAlign: 'center', color: '#64748b', fontSize: '0.875rem' }}>
                  No matching products found. Try a different search term.
                </div>
              ) : (
                productOptions.map((opt) => {
                  const isSelected = selectedProduct?.value === opt.value;
                  return (
                    <div
                      key={`${opt.value}-${opt.variation_id}`}
                      onClick={() => setSelectedProduct(opt)}
                      style={{
                        padding: '0.75rem 1rem',
                        borderBottom: '1px solid #f1f5f9',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        backgroundColor: isSelected ? '#eff6ff' : 'transparent',
                        borderLeft: isSelected ? '4px solid #3b82f6' : '4px solid transparent',
                        transition: 'background-color 0.15s ease'
                      }}
                    >
                      <div>
                        <div style={{ fontWeight: isSelected ? 600 : 500, color: isSelected ? '#1e40af' : '#1e293b', fontSize: '0.9rem' }}>
                          {opt.label}
                        </div>
                        {opt.sku && (
                          <span style={{ fontSize: '0.75rem', color: '#64748b', marginRight: '8px' }}>
                            SKU: {opt.sku}
                          </span>
                        )}
                      </div>
                      {isSelected && (
                        <span style={{ backgroundColor: '#3b82f6', color: 'white', width: '22px', height: '22px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem', fontWeight: 'bold' }}>
                          ✓
                        </span>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>

        {/* Modal Footer */}
        <div 
          style={{ 
            display: 'flex', 
            justifyContent: 'flex-end', 
            gap: '0.75rem', 
            padding: '1rem 1.5rem', 
            backgroundColor: '#f8fafc', 
            borderTop: '1px solid #e2e8f0',
            flexShrink: 0
          }}
        >
          <button
            type="button"
            onClick={onClose}
            style={{ 
              padding: '0.6rem 1.2rem', 
              border: '1px solid #cbd5e1', 
              backgroundColor: '#ffffff', 
              borderRadius: '8px', 
              cursor: 'pointer',
              fontWeight: 500,
              color: '#475569',
              fontSize: '0.875rem'
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={!selectedProduct}
            style={{ 
              padding: '0.6rem 1.25rem', 
              border: 'none', 
              backgroundColor: selectedProduct ? '#3b82f6' : '#94a3b8', 
              color: 'white', 
              borderRadius: '8px', 
              cursor: selectedProduct ? 'pointer' : 'not-allowed', 
              fontWeight: 600,
              fontSize: '0.875rem',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              boxShadow: selectedProduct ? '0 4px 6px -1px rgba(59, 130, 246, 0.3)' : 'none'
            }}
          >
            🔗 Confirm Mapping
          </button>
        </div>
      </div>
    </div>
  );
};

export default AIProductMapModal;

